import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { resolveImage } from "@/lib/api";
import { BlurImage } from "@/components/BlurImage";

/**
 * ProductCard
 * - Displays a product tile in the shop grid / homepage featured row.
 * - `variantImageIndex` (optional): overrides the default images[0] and shows a specific
 *   color-variant image (used by Shop.jsx when a query matches a color OR when splitting
 *   a multi-color product into separate variant cards).
 * - `variantLabel` (optional): the color name to show below the title when displaying a variant.
 *
 * Perf note (2026-02-16): We now (a) pass the full product object through `Link`
 *   state so the PDP can render instantly without waiting for /api/products/{id},
 *   and (b) preload the PDP main image on `pointerenter` (hover on desktop, tap
 *   start on mobile) so by the time the click resolves and the PDP mounts, the
 *   larger `w=1200` variant is already sitting in the browser cache.
 */
export default function ProductCard({ product, index = 0, variantImageIndex = 0, variantLabel = null, hideFeaturedBadge = false }) {
  const imageIdx = Math.max(0, Math.min((product.images?.length || 1) - 1, variantImageIndex || 0));
  const img = product.images?.[imageIdx] || product.images?.[0];

  const hasMrp =
    product.original_price != null &&
    Number(product.original_price) > Number(product.price);
  const discountPct = hasMrp
    ? Math.round(((Number(product.original_price) - Number(product.price)) / Number(product.original_price)) * 100)
    : 0;

  const to = variantLabel
    ? `/product/${product.id}?color=${encodeURIComponent(variantLabel)}`
    : `/product/${product.id}`;

  // Warm up the PDP main image the moment a user hovers / starts a tap. Fired
  // only once per card mount (guarded by the ref) so we don't spam the network.
  const prewarmed = React.useRef(false);
  const prewarmPdpImage = React.useCallback(() => {
    if (prewarmed.current) return;
    prewarmed.current = true;
    if (!img) return;
    // Kick off the fetch via a hidden Image() — the response lands in the
    // browser's in-memory cache and the PDP <img src> at the same URL reuses it.
    try {
      const preload = new window.Image();
      preload.decoding = "async";
      preload.src = resolveImage(img, { w: 1200 });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("ProductCard prewarm failed", e);
    }
  }, [img]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.7, delay: (index % 4) * 0.05, ease: [0.2, 0.7, 0.2, 1] }}
      className="pcard group"
      data-testid={`product-card-${product.id}${variantLabel ? `-${variantLabel.toLowerCase()}` : ""}`}
    >
      <Link
        to={to}
        state={{ product, variantImageIndex: imageIdx, variantLabel }}
        onPointerEnter={prewarmPdpImage}
        onTouchStart={prewarmPdpImage}
        className="block"
      >
        <div className="relative overflow-hidden bg-white aspect-square">
          {img ? (
            <BlurImage
              src={img}
              w={600}
              wrapperClassName="w-full h-full"
              alt={`${product.name}${variantLabel ? ` — ${variantLabel}` : ""} — ${product.category} — Star Jewels artificial jewelry`}
              className="pcard-img w-full h-full object-contain bg-white"
              // Above-the-fold cards (first 4 in the grid) load eagerly + hi-prio
              // so the first paint of the shop grid isn't held back by lazy-load.
              // React expects camelCase `fetchPriority` here; the attribute still
              // lands as `fetchpriority` in the DOM.
              loading={index < 4 ? "eager" : "lazy"}
              fetchPriority={index < 4 ? "high" : "auto"}
              decoding="async"
              width="600"
              height="600"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-sj-muted text-xs">No image</div>
          )}

          {/* Top-left corner badges */}
          <div className="absolute top-3 left-3 flex flex-col items-start gap-1.5">
            {product.featured && !hideFeaturedBadge && (
              <span className="bg-white/90 text-sj-goldDark text-[10px] tracking-[0.2em] uppercase font-semibold px-2.5 py-1 rounded-full">
                Featured
              </span>
            )}
            {hasMrp && discountPct >= 1 && (
              <span
                className="bg-sj-rose text-white text-[10px] tracking-[0.14em] uppercase font-semibold px-2 py-0.5 rounded-full"
                data-testid={`pc-discount-${product.id}`}
              >
                {discountPct}% off
              </span>
            )}
          </div>

          {/* Top-right — Low stock badge */}
          {product.low_stock && !product.out_of_stock && (
            <span
              className="absolute top-3 right-3 bg-sj-ink text-sj-gold text-[10px] tracking-[0.14em] uppercase font-semibold px-2 py-0.5 rounded-full"
              data-testid={`pc-lowstock-${product.id}`}
            >
              Few pieces left
            </span>
          )}
        </div>
        <div className="pt-4 pb-2">
          <h3 className="pcard-title font-display text-[17px] text-sj-ink">
            {product.name}
            {variantLabel && (
              <span className="ml-1.5 text-xs uppercase tracking-[0.14em] text-sj-goldDark align-middle">
                · {variantLabel}
              </span>
            )}
          </h3>
          <div className="mt-1 flex items-baseline gap-2 font-body">
            <span className="text-sj-ink font-medium" data-testid={`pc-price-${product.id}`}>
              ₹{Number(product.price).toLocaleString("en-IN")}
            </span>
            {hasMrp && (
              <span className="text-xs text-sj-muted line-through" data-testid={`pc-mrp-${product.id}`}>
                ₹{Number(product.original_price).toLocaleString("en-IN")}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}
